// Template library (plan A8, B6): a Penyedia sees the Penyedia templates in
// the four Penyedia stages, a Non-Penyedia role the government templates in
// the six stages of the cycle. The definitions come from the API.
// For a Penyedia the page is the Generator Dokumen Penyedia of Simulasi
// Pluto v4: a Paket aktif picker (its paket projects) whose data fills every
// Unduh .docx and whose metode and jenis filter the list (sanggah only for
// Tender/Seleksi, sanggah banding only for a Tender Pekerjaan Konstruksi),
// then Keluaran dari proses Penyedia (../penyedia/SupplierOutputs.tsx).
// Backup Project saves the Paket aktif (or every paket) with its checklist
// and work tools as a .json file (../penyedia/backup.ts).
//   tp_header   plain spacing    h1, count, what a draft is
//   tp_toolbar  divided section  search and tahap
//   tp_groups   rows             one group per stage; each row a template with its
//                                category, note, Unduh .docx (the Word skeleton
//                                at once) and Susun dengan Pluto
//   tp_dialog   dialog           the template's sections and an optional project;
//                                Susun draf starts the conversation and opens it;
//                                Unduh .docx with a PBJ project's paket data, or for
//                                a Penyedia a paket project's data and work tools
//   tp_outputs  region         Non-Penyedia only: Keluaran dari alur PBJ, the
//                                PBJ activities' outputs (../pbj/PbjOutputs.tsx)
//   tp_history  region           Non-Penyedia only: Riwayat pembuatan dokumen
//                                (../pbj/RecentDocs.tsx)
// The draft is a chat conversation: Pluto answers with a document card that
// downloads as DOCX, inside the chosen project when there is one.
import { useState } from "react";
import * as API from "../api";
import { formatCount } from "../lib/format";
import { canDocx, useDocx } from "../pbj/docx";
import { PbjOutputs } from "../pbj/PbjOutputs";
import { RecentDocs } from "../pbj/RecentDocs";
import { navigate } from "../router";
import { useChatShell } from "../shell/chat-context";
import { useFeatures } from "../shell/features";
import { Dialog, EmptyState, Notice, PageHeader, SearchInput, SectionHead, SkeletonRows, useAsync, useTitle } from "../ui/components";
import { Glyph } from "../ui/icons";
import { conversationPath } from "./Chat";
import { templateApplies } from "../project/work/StageDocs";
import { SupplierOutputs } from "../penyedia/SupplierOutputs";
import { downloadPaketBackup } from "../penyedia/backup";

function fold(text: string): string {
  return text.toLowerCase().normalize("NFKD").replace(/\p{M}/gu, "");
}

export function filterTemplates(list: API.TemplateItem[], q: string, stage: string): API.TemplateItem[] {
  const terms = fold(q).split(/\s+/).filter(Boolean);
  return list.filter((t) => {
    if (stage && t.stage !== stage) return false;
    const hay = fold([t.title, t.description, t.categoryLabel].join(" "));
    return terms.every((term) => hay.includes(term));
  });
}

function DraftDialog({ template, government, onClose }: { template: API.TemplateItem | null; government: boolean; onClose: () => void }) {
  const features = useFeatures();
  const shell = useChatShell();
  const projectsOn = features.has("projects");
  const projects = useAsync(() => (projectsOn ? API.listProjects() : Promise.resolve([] as API.Project[])), [projectsOn]);
  const [projectId, setProjectId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const docx = useDocx();
  // Unduh .docx at once, with the data of a PBJ project (Non-Penyedia) or a paket project (Penyedia).
  const docxOn = canDocx(features);
  const docxKind = government ? "pbj" : "paket";
  const docxProjectId = (projects.data ?? []).find((p) => p.id === projectId && p.kind === docxKind)?.id;

  const close = () => {
    if (busy || docx.busy) return;
    setError(null);
    onClose();
  };

  const submit = async () => {
    if (!template || busy) return;
    setBusy(true);
    setError(null);
    try {
      const draft = await API.draftTemplate(template.id, projectId || undefined);
      shell?.bumpHistory();
      navigate(conversationPath(draft.conversationId, draft.projectId ?? undefined));
    } catch (err) {
      // A draft that failed at Pluto is still stored: open it, Coba lagi answers there.
      if (err instanceof API.ApiError && err.conversationId) {
        shell?.bumpHistory();
        navigate(conversationPath(err.conversationId, projectId || undefined));
        return;
      }
      setError(API.errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={template !== null}
      title={template ? `Susun ${template.title}` : ""}
      description={template?.description}
      onClose={close}
      busy={busy}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={close} disabled={busy}>
            Batal
          </button>
          {docxOn && template ? (
            <button
              type="button"
              className="btn btn-secondary"
              disabled={busy || docx.busy !== null}
              aria-busy={docx.busy !== null || undefined}
              onClick={() => void docx.start(template.id, () => API.downloadTemplateDocx(template.id, docxProjectId))}
            >
              {docx.busy ? "Menyiapkan" : "Unduh .docx"}
            </button>
          ) : null}
          <button type="button" className="btn btn-primary" onClick={() => void submit()} disabled={busy || docx.busy !== null} aria-busy={busy || undefined}>
            {busy ? "Pluto menyusun draf" : "Susun draf"}
          </button>
        </>
      }
    >
      {template ? (
        <div className="tp-dialog">
          {template.note ? <p className="field-hint">{template.note}</p> : null}
          <p className="tp-dialog-lead">Pluto menyusun draf dengan bagian berikut, lalu Anda dapat mengunduhnya sebagai DOCX:</p>
          <ol className="tp-sections">
            {template.sections.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ol>
          {projectsOn ? (
            <div className="field">
              <label htmlFor="tp-project">Project</label>
              <select id="tp-project" value={projectId} onChange={(e) => setProjectId(e.currentTarget.value)} disabled={busy}>
                <option value="">Tanpa project</option>
                {(projects.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <p className="field-hint">
                Di dalam project, Pluto memakai data paket dan berkas project tersebut.
                {docxOn
                  ? government
                    ? " Unduh .docx langsung menyiapkan kerangka Word tanpa Pluto, dengan data paket bila project-nya project PBJ."
                    : " Unduh .docx langsung menyiapkan kerangka Word tanpa Pluto, berisi data paket, matriks persyaratan, RAB, Quality Gate, dan monitoring kontrak project yang dipilih."
                  : ""}
              </p>
            </div>
          ) : null}
          {error ? (
            <Notice tone="danger" title="Draf belum dapat disusun">
              <p>{error}</p>
            </Notice>
          ) : null}
          {docx.error ? (
            <Notice tone="danger" title="Dokumen belum dapat diunduh">
              <p>{docx.error.message}</p>
            </Notice>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  );
}

export function TemplatesScreen() {
  const library = useAsync(() => API.getTemplates(), []);
  const data = library.data;
  const penyedia = data?.side === "penyedia";
  useTitle("Generate Dokumen");
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("");
  const [open, setOpen] = useState<API.TemplateItem | null>(null);
  const features = useFeatures();
  const docx = useDocx();
  // A Penyedia's Paket aktif: its data fills Unduh .docx; its metode and jenis filter the list.
  const projectsOn = features.has("projects");
  const projects = useAsync(() => (penyedia && projectsOn ? API.listProjects() : Promise.resolve([] as API.Project[])), [penyedia, projectsOn]);
  const pakets = (projects.data ?? []).filter((p) => p.kind === "paket");
  const [paketId, setPaketId] = useState("");
  const paket = pakets.find((p) => p.id === paketId) ?? null;
  const [backingUp, setBackingUp] = useState(false);
  const [backupError, setBackupError] = useState<string | null>(null);
  const backup = async () => {
    setBackingUp(true);
    setBackupError(null);
    try {
      await downloadPaketBackup(paket ? [paket] : pakets);
    } catch (err) {
      setBackupError(API.errorMessage(err));
    } finally {
      setBackingUp(false);
    }
  };

  const government = data?.side === "pemerintah";
  const docxOn = canDocx(features);
  const all = (data?.templates ?? []).filter((t) => !penyedia || !paket || templateApplies(t, paket));
  const shown = filterTemplates(all, q, stage);
  const groups = (data?.stages ?? []).map((s) => ({ stage: s, items: shown.filter((t) => t.stage === s.id) })).filter((g) => g.items.length > 0);
  const lead = !data
    ? "Memuat template"
    : data.side === "penyedia"
      ? `Buat draf Word dari ${formatCount(all.length)} template administrasi, kualifikasi, teknis, harga, sanggah, kontrak, dan serah terima. Draf tidak menggantikan dokumen penawaran final; isi, bukti, dan format wajib mengikuti dokumen pemilihan atau adendum yang berlaku.`
      : `${formatCount(all.length)} template dokumen pengadaan. Bagian bertanda [LENGKAPI] wajib diisi dan direviu sebelum dipakai.`;

  return (
    <>
      <PageHeader
        divided
        title={penyedia ? "Generate Dokumen Penyedia" : "Generate Dokumen"}
        lead={lead}
        actions={
          penyedia && projectsOn ? (
            <button type="button" className="btn btn-secondary" disabled={backingUp || pakets.length === 0} aria-busy={backingUp || undefined} onClick={() => void backup()}>
              <Glyph name="documentDownload" size={18} />
              <span>{backingUp ? "Menyiapkan" : "Backup Project"}</span>
            </button>
          ) : undefined
        }
      />
      {backupError ? (
        <Notice tone="danger" title="Backup belum tersimpan">
          <p>{backupError}</p>
        </Notice>
      ) : null}
      {penyedia && projectsOn ? (
        <p className="pks-caption">
          {paket
            ? `Paket aktif: ${paket.paketName?.trim() || paket.name}. Backup Project menyimpan paket ini beserta checklist dan alat kerjanya sebagai file JSON.`
            : "Pilih paket aktif agar Word terisi data paketnya. Tanpa paket aktif, Backup Project menyimpan semua paket."}
        </p>
      ) : null}

      <section className="toolbar" aria-label="Cari template">
        <div className="field-row" data-cols="3">
          {penyedia && projectsOn ? (
            <div className="field">
              <label htmlFor="tp-paket">Paket aktif</label>
              <select id="tp-paket" value={paketId} onChange={(e) => setPaketId(e.currentTarget.value)}>
                <option value="">Tanpa paket</option>
                {pakets.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.paketName?.trim() || p.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <div className="field">
            <label htmlFor="tp-q">Cari template</label>
            <SearchInput id="tp-q" value={q} onChange={setQ} placeholder="Nama dokumen atau kategori" />
          </div>
          <div className="field">
            <label htmlFor="tp-stage">Tahap</label>
            <select id="tp-stage" value={stage} onChange={(e) => setStage(e.currentTarget.value)}>
              <option value="">Semua tahap</option>
              {(data?.stages ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {library.loading && !data ? (
        <SkeletonRows count={6} label="Memuat template" />
      ) : library.error ? (
        <Notice tone="danger" title="Template belum dapat dimuat" action={<button type="button" className="btn btn-secondary" onClick={library.reload}>Muat ulang</button>}>
          <p>{API.errorMessage(library.error)}</p>
        </Notice>
      ) : all.length === 0 ? (
        <EmptyState icon="documentText" title="Belum ada template" body="Template untuk peran Anda belum tersedia." />
      ) : groups.length === 0 ? (
        <EmptyState icon="filter" title="Tidak ada template yang cocok" body="Ubah kata kunci atau pilih semua tahap." />
      ) : (
        <div className="kb-groups tp-groups">
          {groups.map((g) => (
            <section key={g.stage.id} className="region tp-group" aria-labelledby={`tp-${g.stage.id}`}>
              {/* The stage's place in the PBJ cycle, so the groups read as a flow. */}
              <div className="tp-group-head">
                <span className="tp-step num" aria-hidden="true">
                  {(data?.stages ?? []).findIndex((s) => s.id === g.stage.id) + 1}
                </span>
                <SectionHead id={`tp-${g.stage.id}`} title={`${g.stage.label} (${formatCount(g.items.length)})`} />
              </div>
              <ul className="rows">
                {g.items.map((t) => (
                  <li key={t.id} className="row tp-row">
                    <span className="tp-icon" aria-hidden="true">
                      <Glyph name="documentText" size={18} />
                    </span>
                    <span className="row-main">
                      <span className="tp-title-line">
                        <span className="row-title">{t.title}</span>
                        <span className="badge tp-cat">{t.categoryLabel}</span>
                      </span>
                      <span className="row-meta">{t.description}</span>
                      {t.note ? <span className="row-meta">{t.note}</span> : null}
                      {docx.error?.key === t.id ? (
                        <span className="pk-item-error" role="alert">
                          <Glyph name="danger" size={16} />
                          <span>{docx.error.message}</span>
                        </span>
                      ) : null}
                    </span>
                    <span className="row-side tp-actions">
                      <button type="button" className="btn btn-sm tp-draft" onClick={() => setOpen(t)} aria-label={`Susun ${t.title} dengan Pluto`}>
                        <Glyph name="docAdd" size={16} />
                        <span>Susun dengan Pluto</span>
                      </button>
                      {docxOn ? (
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          disabled={docx.busy !== null}
                          aria-busy={docx.busy === t.id || undefined}
                          aria-label={`Unduh .docx ${t.title}`}
                          onClick={() => void docx.start(t.id, () => API.downloadTemplateDocx(t.id, paket?.id))}
                        >
                          <Glyph name="documentDownload" size={16} />
                          <span>{docx.busy === t.id ? "Menyiapkan" : "Unduh .docx"}</span>
                        </button>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      {government ? <PbjOutputs /> : null}
      {penyedia ? <SupplierOutputs projectId={paket?.id ?? null} /> : null}
      {government ? <RecentDocs /> : null}

      <DraftDialog key={open?.id ?? "none"} template={open} government={government} onClose={() => setOpen(null)} />
    </>
  );
}
